<template>
  <view-container
    header
    header-button
    class="view-invoices"
    title="Detalhes de notas fiscais"
    subtitle="Veja os detalhes e administre as notas fiscais"
    :breadcrumbs="[
      { title: 'Ferramentas', link: 'tools' },
      { title: 'Notas fiscais', link: 'tools-invoices' },
      { title: 'Detalhes' }
    ]"
  >
    <template #header-button>
      <router-link :to="{ name: 'tools-enotas' }" class="integration-link pt-6">
        Ir para integração
        <i class="fa fa-arrow-up-right-from-square ml-2" />
      </router-link>
    </template>

    <div v-if="cardsLoading" class="text-center my-9">
      <b-spinner variant="primary" />
    </div>

    <div class="row justify-content-between my-6 px-3" v-else>
      <div class="col-lg-4 px-3 mt-3">
        <invoices-card
          name="Total notas fiscais"
          icon="fa-file-invoice-dollar"
          :amount="invoicesTotal.total_invoices"
          :value="formatMoney(invoicesTotal.amount_invoices)"
        />
      </div>
      <div class="col-lg-4 px-3 mt-3">
        <invoices-card
          name="Enviadas"
          icon="fa-file-export"
          :amount="invoicesSent.total_invoices"
          :value="formatMoney(invoicesSent.amount_invoices)"
        />
      </div>
      <div class="col-lg-4 px-3 mt-3">
        <invoices-card
          name="Pendentes"
          icon="fa-clock"
          :amount="invoicesPending.total_invoices"
          :value="formatMoney(invoicesPending.amount_invoices)"
        />
      </div>
    </div>

    <div class="row justify-content-between">
      <div
        class="d-flex align-items-center order-1 order-lg-0 col-lg-6 mt-5 mb-lg-0"
      >
        <span class="table-quantity-counter mr-5">
          Total: {{ pagination.count || 0 }}
        </span>
        <button class="btn p-0 table-refresh" @click="refresh">
          <i class="fa-solid fa-refresh" />
        </button>
      </div>

      <div class="col-lg-6 d-flex align-items-center order-0 order-lg-1">
        <search-filter
          new-design
          class="flex-fill"
          placeholder="Pesquisar"
          :show-results-dropdown="false"
          @search="searching"
        />

        <filter-button
          button-class="btn  btn-sm btn-outline-primary btn-new-outline-primary p-3 btn-square btn-40 ml-3"
          description
        >
          <template #default="{ close }">
            <invoices-filters
              v-model="filters"
              @input="close"
              @close="close"
              @onFiltersApply="onFiltersApply"
            />
          </template>
        </filter-button>

        <div class="ml-3">
          <button
            class="d-none d-lg-flex btn btn-primary btn-primary px-5 btn-sm btn-40"
            @click="exportCSV"
          >
            <loading :show="exportLoading">
              <i class="fa fa-download mr-6" />
              Exportar
            </loading>
          </button>

          <button
            class="btn btn-primary d-lg-none btn-primary btn-square px-0 btn-sm btn-40"
            @click="exportCSV"
          >
            <loading :show="exportLoading">
              <i class="fa fa-download" />
            </loading>
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center my-9">
      <b-spinner variant="primary" />
    </div>
    <div
      v-else-if="!invoices.length"
      class="container centralize-row text-center pt-6"
    >
      <div class="row justify-content-center">
        <div class="col-lg-5">
          <img
            class="d-block mb-6 mx-auto"
            src="@/assets/images/home/welcome.svg"
          />
          <template v-if="filters.search">
            <h5>Nenhum resultado encontrado</h5>
          </template>

          <template v-else>
            <h5>Você ainda não enviou notas!</h5>
          </template>
        </div>
      </div>
    </div>
    <div v-else>
      <!-- Desktop -->
      <data-table
        new-design
        class="d-none d-lg-block"
        columnsFr="0.5fr 1fr 0.7fr 0.5fr 0.5fr 0.5fr 0.3fr 0.1fr"
        :data="invoices"
        :columns="columns"
      >
        <template #default="{ item }">
          <span>
            {{ item.paid_at | formatDate }}
          </span>
          <div class="crop">
            <div class="fw-5">
              {{ item.shopper_name }}
            </div>
            <div class="text-secondary">
              {{ item.shopper_email }}
            </div>
          </div>
          <div class="crop">
            <div class="fw-5">{{ item.product_name }}</div>
          </div>
          <span>R$ {{ formatMoney(item.amount) }}</span>
          <span>
            {{ item.payment_method }}
          </span>
          <span> {{ item.send_at | formatDate }}</span>
          <span>
            <span :class="formatStatusClass(item.status)">
              {{ item.status | formatStatus }}
            </span>
          </span>
          <div class="d-flex align-items-center justify-content-end">
            <actions-dropdown
              :left="false"
              :right="true"
              :actions="actions"
              @open-details="openDetails(item)"
            />
          </div>
        </template>
      </data-table>
      <!-- Desktop end -->
      <!-- Mobile -->
      <data-table
        new-design
        class="d-lg-none"
        :data="invoices"
        :firstColumnFr="2"
        :lastColumnFr="0.3"
        :columns="['', '']"
      >
        <template #default="{item}">
          <div class="d-flex flex-column responsive-table py-5">
            <span>
              <strong>Comprador: </strong>
              {{ item.shopper_name }}
            </span>
            <span>
              <strong>Produto: </strong>
              {{ item.product_name }}
            </span>
            <span>
              <strong>Valor: </strong>R$
              {{ formatMoney(item.amount) }}
            </span>
            <span>
              <strong>Status: </strong>
              <span :class="formatStatusClass(item.status)">
                {{ item.status | formatStatus }}
              </span>
            </span>
          </div>
          <div class="d-flex align-items-top justify-content-end">
            <actions-dropdown
              :left="false"
              :right="true"
              :actions="actions"
              @open-details="openDetails(item)"
            />
          </div>
        </template>
      </data-table>
    </div>
    <div class="d-flex justify-content-end mb-6 mt-3">
      <paginator
        ref="paginator"
        v-show="invoices.length && !loading"
        :pagination="pagination"
        @on-update="onPaginationUpdate"
      />
    </div>
    <invoice-details
      v-model="modal"
      :invoice="focusedInvoice"
      @refresh="refresh"
    />
  </view-container>
</template>
<script>
import { SALE_PAY_METHOD_NAME_MAP } from "@/constants/sales.js";
import { clearObject, datesString, formatMoney } from "@/functions/helpers";

import InvoiceDetails from "@/components/sale/InvoiceDetails";
import InvoicesCard from "@/components/sale/InvoicesCard.vue";
import InvoicesFilters from "@/components/sale/InvoicesFilters";

import moment from "moment";
import { mapState } from "vuex";

export default {
  components: {
    InvoicesCard,
    InvoicesFilters,
    InvoiceDetails
  },
  data() {
    return {
      invoicesTotal: {
        total_invoices: 0,
        amount_invoices: ""
      },
      invoicesSent: {
        total_invoices: 0,
        amount_invoices: ""
      },
      invoicesPending: {
        total_invoices: 0,
        amount_invoices: ""
      },
      loading: false,
      cardsLoading: false,
      exportLoading: false,
      modal: false,
      focusedInvoice: null,
      actions: [
        {
          text: "Detalhes",
          icon: "icon-exclamation-outline",
          confirmation: {
            enable: false,
            text: ""
          },
          event: "open-details"
        }
      ],
      columns: [
        "Competência",
        "Comprador",
        "Produto",
        "Valor",
        "Pagamento",
        "Emitida",
        "Status",
        ""
      ],
      filters: {
        offset: 0,
        status: "",
        search: "",
        product_id: "",
        send_at_after: "",
        send_at_before: "",
        paid_at_after: "",
        paid_at_before: ""
      }
    };
  },
  filters: {
    formatStatus(val) {
      let options = {
        sent: "Enviado",
        pending: "Pendente",
        failed: "Falha",
        canceled: "Cancelado"
      };
      return options[val];
    },
    formatDate(val) {
      if (val) val = val.replace("Z", "");
      return val ? moment(val).format("DD/MM/YYYY") : "-";
    }
  },
  computed: {
    ...mapState("product", ["products"]),
    ...mapState("invoiceNfe", ["invoices", "pagination"])
  },
  watch: {
    filters: {
      handler() {
        this.dispatch();
      },
      deep: true
    }
  },
  mounted() {
    this.dispatch();
    this.getTotals();
  },
  methods: {
    formatStatusClass(val) {
      let options = {
        sent: "active-badge",
        pending: "draft-badge",
        failed: "inactive-badge",
        canceled: "incomplete-badge"
      };
      return options[val];
    },
    getPayMethod(payment_method) {
      if (payment_method) {
        return SALE_PAY_METHOD_NAME_MAP[payment_method.toLowerCase()];
      }
      return "-";
    },
    async exportCSV() {
      try {
        this.exportLoading = true;

        let params = { ...this.filters, export: true };

        const CSVContent = await this.$store
          .dispatch("invoiceNfe/getInvoicesCSV", params)
          .then();
        var a = document.createElement("a");

        a.href = `data:text/csv;charset=utf-8,${CSVContent.replaceAll(
          "#",
          "%23"
        )}`;
        a.setAttribute(
          "download",
          `invoices_${moment().format("YYYYMMDDhhmmss")}.csv`
        );
        a.click();
      } catch (e) {
      } finally {
        this.exportLoading = false;
      }
    },
    searching(v) {
      this.filters.search = v;
      this.dispatch();
    },
    openDetails(item) {
      this.focusedInvoice = item;
      this.modal = true;
    },
    refresh() {
      this.dispatch();
    },
    dispatch() {
      this.loading = true;
      this.$store
        .dispatch("invoiceNfe/getInvoices", clearObject(this.filters))
        .finally(() => {
          this.loading = false;
        });
    },
    getTotals() {
      this.cardsLoading = true;
      Promise.all([
        this.$store
          .dispatch("invoiceNfe/getInvoicesTotal")
          .then(res => (this.invoicesTotal = res)),
        this.$store
          .dispatch("invoiceNfe/getInvoicesTotal", { status: "sent" })
          .then(res => (this.invoicesSent = res)),
        this.$store
          .dispatch("invoiceNfe/getInvoicesTotal", { status: "pending" })
          .then(res => (this.invoicesPending = res))
      ]).finally(() => (this.cardsLoading = false));
    },
    onPaginationUpdate(offset) {
      this.filters.offset = offset;
      this.refresh();
    },
    onFiltersApply() {
      this.$refs.paginator.reset();
    },
    formatMoney,
    datesString
  }
};
</script>
<style lang="scss" scoped>
.integration-link {
  color: #360087ed;
  font-weight: 600;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
}
</style>
